import { GetAllBankQuestions } from "../../api/QuestionBank/GetAllBankQuestions";
import { useQuery } from "@tanstack/react-query";
import AddIcon from "../../components/Icons/AddIcon";
import Button from "../../components/Shared/Button";
import { useState } from "react";
import Spinner from "../../components/Shared/Spinner";
import AddBankQuestions from "../../components/Dashboard/AddQuiz/AddBankQuestions";
import { getCourses } from "../../api/Courses/GetAllCourses";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import { GetCourseById } from "../../api/Courses/GetCourseById";

const QuestionBank = () => {
    const [showAddCourse, setShowAddCourse] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCourse, setSelectedCourse] = useState<option | null>(null);
    const [selectedChapter, setSelectedChapter] = useState<option | null>(null);
    const [selectedLecture, setSelectedLecture] = useState<option | null>(null);

    const limit = 9;

    // Fetch all courses
    const { data: CoursesResponse } = useQuery({
        queryFn: getCourses,
        queryKey: ["courses"],
        staleTime: 0,
        refetchInterval: 0,
    });

    const courses = CoursesResponse?.data?.coursesData || [];
    const courseOptions = courses.map((course) => ({
        id: course.id,
        slug: course.slug,
        name: course.name,
    }));
    const { data: CourseByIdDTO } = useQuery({
        queryFn: () => GetCourseById(selectedCourse?.slug || ''),
        queryKey: ["course", selectedCourse?.slug],
        staleTime: 0,
        refetchInterval: 0,
        enabled: !!selectedCourse, 
    });

    const chapters = CourseByIdDTO?.data?.chapters || [];
    const chapterOptions = chapters.map((chapter) => ({
        id: chapter.id,
        name: chapter.name || '',
    }));
    
    // Fetch lectures based on selected chapter
    const selectedChapterDetails = chapters.find(chapter => chapter.id === selectedChapter?.id);
    const lectures = selectedChapterDetails?.lectures || [];
    const lectureOptions = lectures.map((lecture) => ({
        id: lecture.id,
        name: lecture.title,
    }));

    // Fetch bank questions
    const { data: Bank, isLoading, isFetching } = useQuery({
        queryFn: () => GetAllBankQuestions({
            page: currentPage,
            limit,
            courseId: selectedCourse?.id,
            chapterId: selectedChapter?.id,
            lectureId: selectedLecture?.id,
        }),
        queryKey: ["bank", currentPage, selectedCourse?.id, selectedChapter?.id, selectedLecture?.id],
        staleTime: 0,
        refetchInterval: 0,
    });

    const BankQuestions = Bank?.data?.questions;
    const totalPages = Bank?.data?.meta?.totalPages || 0;

    const handleAddCourseSuccess = () => {
        setShowAddCourse(false);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSelectedCourse(null);
        setSelectedChapter(null);
        setSelectedLecture(null);
    };

    if (isLoading || isFetching)
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Spinner />
            </div>
        );

    return (
        <div>
            {showAddCourse ? (
                <AddBankQuestions onSuccess={handleAddCourseSuccess} />
            ) : (
                <div>
                    <div className="flex justify-between items-center py-7">
                        <h1 className="text-burntBlack font-bold text-[32px]">Bank Questions</h1>
                        <Button
                            onClick={() => setShowAddCourse(true)}
                            className="!h-12 w-56 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
                            variant="primary"
                        >
                            <AddIcon className="mr-1.5" />
                            Add New Bank Questions
                        </Button>
                    </div>
                    <div className="bg-white rounded-lg p-6">
                    <div className="flex justify-end mb-4">
                            <Button
                                onClick={handleClearFilters}
                                className="before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-32 !border-primary !border-2 text-primary"
                                variant="transparent"
                            >
                                Clear Filters
                            </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <Selectbox
                                title="Filter By Course"
                                name="course"
                                placeholder="Select a course"
                                selectOptions={courseOptions}
                                selected={selectedCourse}
                                setSelected={setSelectedCourse}
                            />
                            {selectedCourse && (
                                <Selectbox
                                    title="Filter By Chapter"
                                    name="chapter"
                                    placeholder="Select a chapter"
                                    selectOptions={chapterOptions}
                                    selected={selectedChapter}
                                    setSelected={setSelectedChapter}
                                />
                            )}
                            {selectedChapter && (
                                <Selectbox
                                    title="Filter By Lecture"
                                    name="lecture"
                                    placeholder="Select a lecture"
                                    selectOptions={lectureOptions}
                                    selected={selectedLecture}
                                    setSelected={setSelectedLecture}
                                />
                            )}
                        </div>
                        <div className="grid-cols-3 grid gap-5 mt-6">
                            {BankQuestions?.map((bank, index) => (
                                <div className="p-4 border border-black/15 rounded-lg" key={index}>
                                    <h1 className="font-bold text-xl mb-4">{bank.question}</h1>
                                    <div className="space-y-2">
                                        {bank.choices.map((answer) => (
                                            <div key={answer.id}>
                                                <p className={`${answer.isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                                                    {answer.answer}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                        <Button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-32 !border-primary !border-2 text-primary"
                            variant="transparent"
                        >
                            Previous
                        </Button>
                        <p>Page {currentPage} of {totalPages}</p>
                        <Button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-32 !border-primary !border-2 text-primary"
                            variant="transparent"
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionBank;
